/**
 * Pinia store do Canon pessoal (D3 da Proposta Lume, Fase 3).
 *
 * Persiste em localStorage (não no vault — entries de Canon são
 * reflexões pessoais que devem ser instantaneamente acessíveis
 * para o aprendiz; o vault é para dados criptografados do modelo).
 *
 * Para migração futura ao vault (mais seguro), basta mover
 * STORAGE_KEY para vault + adicionar encryption.
 *
 * Constitucional:
 * - Art II §2.5: Canon é do aprendiz.
 * - P-UX-08: entries são reflexões, não scores.
 * - Princípio III: continuidade, não eventos.
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { CanonEntry, CanonEntryKind } from "./types";

const STORAGE_KEY = "hlos.canon.entries.v1";

function loadFromStorage(): CanonEntry[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as CanonEntry[];
  } catch {
    return [];
  }
}

function saveToStorage(entries: CanonEntry[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // ignore (quota exceeded etc.)
  }
}

export const useCanonStore = defineStore("canon", () => {
  const entries = ref<CanonEntry[]>(loadFromStorage());

  const sorted = computed(() =>
    [...entries.value].sort((a, b) => b.createdAt - a.createdAt),
  );

  const byKind = computed(() => {
    const out: Record<CanonEntryKind, CanonEntry[]> = {
      understanding: [],
      question: [],
      changed_mind: [],
      metaphor: [],
      curiosity: [],
      vocabulary: [],
    };
    for (const e of entries.value) {
      out[e.kind].push(e);
    }
    return out;
  });

  function add(entry: Omit<CanonEntry, "id" | "createdAt" | "updatedAt">): CanonEntry {
    const now = Date.now();
    const e: CanonEntry = {
      ...entry,
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now,
    };
    entries.value = [e, ...entries.value];
    saveToStorage(entries.value);
    return e;
  }

  function update(id: string, patch: Partial<CanonEntry>): void {
    entries.value = entries.value.map((e) =>
      e.id === id ? { ...e, ...patch, updatedAt: Date.now() } : e,
    );
    saveToStorage(entries.value);
  }

  function remove(id: string): void {
    entries.value = entries.value.filter((e) => e.id !== id);
    saveToStorage(entries.value);
  }

  function exportMarkdown(): string {
    const lines: string[] = ["# Meu Canon", ""];
    for (const e of sorted.value) {
      const date = new Date(e.createdAt).toISOString().split("T")[0];
      const kindLabel: Record<CanonEntryKind, string> = {
        understanding: "Compreensão",
        question: "Pergunta",
        changed_mind: "Mudei de ideia",
        metaphor: "Metáfora",
        curiosity: "Curiosidade",
        vocabulary: "Vocabulário",
      };
      lines.push(`## ${kindLabel[e.kind]} — ${date}`);
      if (e.topic) lines.push(`*Tópico: ${e.topic}*`);
      lines.push("");
      lines.push(e.text);
      lines.push("");
    }
    return lines.join("\n");
  }

  function clearAll(): void {
    entries.value = [];
    saveToStorage(entries.value);
  }

  return {
    entries,
    sorted,
    byKind,
    add,
    update,
    remove,
    exportMarkdown,
    clearAll,
  };
});
